import React, { useEffect, useState } from "react";
import {
  Table,
  Title,
  Paper,
  Badge,
  Modal,
  Stack,
  Text,
  Group,
  Divider,
  ScrollArea,
  Skeleton,
  Pagination,
  Select,
  Button,
  ActionIcon,
  Avatar,
} from "@mantine/core";
import {
  IconStar,
  IconEdit,
  IconTrash,
  IconUser,
  IconQuote,
  IconCheck,
  IconX,
  IconClock,
} from "@tabler/icons-react";
import { formatRelativeTime } from "../../../utils/formatTime";
import {
  getTestimonials,
  updateTestimonialStatus,
  deleteTestimonial,
  ITestimonialProps,
} from "../../../services/testimonials";

const Testimonials: React.FC = () => {
  const [testimonials, setTestimonials] = useState<ITestimonialProps[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedTestimonial, setSelectedTestimonial] = useState<ITestimonialProps | null>(null);
  const [modalOpened, setModalOpened] = useState(false);
  const [statusModalOpened, setStatusModalOpened] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState<ITestimonialProps | null>(null);
  const [status, setStatus] = useState<string>("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const limit = 10;

  useEffect(() => {
    loadTestimonials();
  }, [page, statusFilter]);

  async function loadTestimonials() {
    setLoading(true);
    try {
      const result = await getTestimonials(page, limit, statusFilter || undefined);
      setTestimonials(result.data || []);
      setTotalPages(Math.ceil((result.totalRecords || 0) / limit));
    } catch (error) {
      console.error("Failed to load testimonials:", error);
    }
    setLoading(false);
  }

  const handleRowClick = (testimonial: ITestimonialProps) => {
    setSelectedTestimonial(testimonial);
    setModalOpened(true);
  };

  const handleStatusUpdate = async () => {
    if (!editingTestimonial || !status) return;
    setLoading(true);
    try {
      await updateTestimonialStatus(
        editingTestimonial.id,
        status as "pending" | "approved" | "rejected"
      );
      setStatusModalOpened(false);
      setEditingTestimonial(null);
      setStatus("");
      loadTestimonials();
    } catch (error) {
      console.error("Failed to update testimonial status:", error);
    }
    setLoading(false);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this testimonial?")) return;
    setLoading(true);
    try {
      await deleteTestimonial(id);
      loadTestimonials();
    } catch (error) {
      console.error("Failed to delete testimonial:", error);
    }
    setLoading(false);
  };

  const openStatusModal = (testimonial: ITestimonialProps) => {
    setEditingTestimonial(testimonial);
    setStatus(testimonial.status);
    setStatusModalOpened(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "approved":
        return "green";
      case "pending":
        return "yellow";
      case "rejected":
        return "red";
      default:
        return "gray";
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getAvatarColor = (name: string) => {
    const colors = [
      "teal",
      "cyan",
      "blue",
      "indigo",
      "violet",
      "grape",
      "pink",
      "red",
      "orange",
      "yellow",
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  return (
    <div className="p-6">
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Title order={2}>Testimonials</Title>
          <Select
            placeholder="Filter by status"
            data={[
              { value: "", label: "All" },
              { value: "pending", label: "Pending" },
              { value: "approved", label: "Approved" },
              { value: "rejected", label: "Rejected" },
            ]}
            value={statusFilter}
            onChange={(value) => {
              setStatusFilter(value || "");
              setPage(1);
            }}
            clearable
            style={{ width: 200 }}
          />
        </Group>

        <Paper shadow="sm" p="md" radius="md" withBorder>
          <ScrollArea>
            {loading ? (
              <Stack gap="md">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} height={60} />
                ))}
              </Stack>
            ) : testimonials.length === 0 ? (
              <Text c="dimmed" ta="center" py="xl">
                No testimonials found
              </Text>
            ) : (
              <Table striped highlightOnHover withTableBorder withColumnBorders>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Testimonial</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Date</Table.Th>
                    <Table.Th>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {testimonials.map((testimonial) => (
                    <Table.Tr
                      key={testimonial.id}
                      style={{ cursor: "pointer" }}
                      onClick={() => handleRowClick(testimonial)}
                    >
                      <Table.Td>
                        <Group gap="sm">
                          <Avatar
                            color={getAvatarColor(testimonial.name)}
                            radius="xl"
                            size="sm"
                          >
                            {getInitials(testimonial.name)}
                          </Avatar>
                          <Text fw={500}>{testimonial.name}</Text>
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Text lineClamp={2} size="sm">
                          {testimonial.text}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge color={getStatusColor(testimonial.status)} variant="light">
                          {testimonial.status}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" c="dimmed">
                          {formatRelativeTime(testimonial.createdAt)}
                        </Text>
                      </Table.Td>
                      <Table.Td onClick={(e) => e.stopPropagation()}>
                        <Group gap="xs">
                          <ActionIcon
                            variant="light"
                            color="blue"
                            onClick={() => openStatusModal(testimonial)}
                          >
                            <IconEdit size={16} />
                          </ActionIcon>
                          <ActionIcon
                            variant="light"
                            color="red"
                            onClick={() => handleDelete(testimonial.id)}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </ScrollArea>

          {totalPages > 1 && (
            <Group justify="flex-end" mt="md">
              <Pagination
                total={totalPages}
                value={page}
                onChange={setPage}
                color="teal"
              />
            </Group>
          )}
        </Paper>
      </Stack>

      {/* View Testimonial Modal */}
      <Modal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        title="Testimonial Details"
        size="lg"
      >
        {selectedTestimonial && (
          <Stack gap="md">
            <Group gap="md">
              <Avatar
                color={getAvatarColor(selectedTestimonial.name)}
                radius="xl"
                size="lg"
              >
                {getInitials(selectedTestimonial.name)}
              </Avatar>
              <div>
                <Text fw={600} size="lg">
                  {selectedTestimonial.name}
                </Text>
                <Text size="xs" c="dimmed">
                  {formatRelativeTime(selectedTestimonial.createdAt)}
                </Text>
              </div>
            </Group>
            <Divider />
            <Badge color={getStatusColor(selectedTestimonial.status)} variant="light">
              {selectedTestimonial.status}
            </Badge>
            <Text size="sm" className="leading-relaxed">
              {selectedTestimonial.text}
            </Text>
          </Stack>
        )}
      </Modal>

      {/* Update Status Modal */}
      <Modal
        opened={statusModalOpened}
        onClose={() => {
          setStatusModalOpened(false);
          setEditingTestimonial(null);
          setStatus("");
        }}
        title="Update Testimonial Status"
      >
        {editingTestimonial && (
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              Update the status of this testimonial
            </Text>
            <Select
              label="Status"
              data={[
                { value: "pending", label: "Pending" },
                { value: "approved", label: "Approved" },
                { value: "rejected", label: "Rejected" },
              ]}
              value={status}
              onChange={(value) => setStatus(value || "")}
              required
            />
            <Group justify="flex-end" mt="md">
              <Button
                variant="light"
                onClick={() => {
                  setStatusModalOpened(false);
                  setEditingTestimonial(null);
                  setStatus("");
                }}
              >
                Cancel
              </Button>
              <Button
                className="bg-teal-600 hover:bg-teal-700"
                onClick={handleStatusUpdate}
                loading={loading}
              >
                Update
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </div>
  );
};

export default Testimonials;

